import React, { useRef, useEffect, useState, useCallback } from 'react';
import { motion, useMotionValue, useSpring, useTransform } from 'framer-motion';
import { FaEnvelope, FaPhone, FaLinkedin, FaGithub, FaPaperPlane } from 'react-icons/fa';
import { personalInfo } from '../../data/skills';
import { sanitizeInput, isValidEmail, formRateLimiter } from '../../utils/security';

const Contact = () => {
  const sectionRef = useRef(null);
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  
  const rotateX = useSpring(useTransform(mouseY, [-300, 300], [8, -8]), { stiffness: 100, damping: 30 });
  const rotateY = useSpring(useTransform(mouseX, [-300, 300], [-8, 8]), { stiffness: 100, damping: 30 });
  
  const handleMouseMove = useCallback((e) => {
    if (!sectionRef.current) return;
    
    try {
      const rect = sectionRef.current.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      mouseX.set(e.clientX - centerX);
      mouseY.set(e.clientY - centerY);
    } catch (error) {
      console.warn('Mouse tracking error:', error);
    }
  }, [mouseX, mouseY]);
  
  useEffect(() => {
    let timeoutId;
    const throttledMouseMove = (e) => {
      if (timeoutId) return;
      timeoutId = setTimeout(() => {
        handleMouseMove(e);
        timeoutId = null;
      }, 16);
    };
    
    window.addEventListener('mousemove', throttledMouseMove, { passive: true });
    return () => {
      window.removeEventListener('mousemove', throttledMouseMove);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [handleMouseMove]);

  const validateForm = () => {
    const newErrors = {};
    
    // Sanitize inputs
    const sanitizedName = sanitizeInput(formData.name);
    const sanitizedEmail = sanitizeInput(formData.email);
    const sanitizedMessage = sanitizeInput(formData.message);
    
    if (!sanitizedName) newErrors.name = 'Name is required';
    if (!sanitizedEmail) {
      newErrors.email = 'Email is required';
    } else if (!isValidEmail(sanitizedEmail)) {
      newErrors.email = 'Email is invalid';
    }
    if (!sanitizedMessage) newErrors.message = 'Message is required';
    if (sanitizedMessage.length < 10) newErrors.message = 'Message too short (minimum 10 characters)';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Rate limiting check
    const clientId = 'contact-form'; // In production, use IP or user ID
    if (!formRateLimiter.isAllowed(clientId)) {
      alert('Too many attempts. Please wait before submitting again.');
      return;
    }
    
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    try {
      // Sanitize data before submission
      const sanitizedData = {
        name: sanitizeInput(formData.name),
        email: sanitizeInput(formData.email),
        message: sanitizeInput(formData.message),
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent.slice(0, 200) // Limited user agent for security
      };
      
      await new Promise(resolve => setTimeout(resolve, 1000));
      console.log('Form submitted:', sanitizedData);
      setFormData({ name: '', email: '', message: '' });
      alert('Message sent successfully!');
    } catch (error) {
      console.error('Form submission error:', error);
      alert('Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Prevent XSS by limiting input length and sanitizing
    const sanitizedValue = value.slice(0, name === 'message' ? 1000 : 100);
    
    setFormData(prev => ({ ...prev, [name]: sanitizedValue }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  return (
    <section id="contact" className="contact-futuristic" ref={sectionRef}>
      <div className="cyber-grid-bg"></div>
      <div className="container">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
          className="section-header-futuristic"
          style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
        >
          <motion.h2 
            className="section-title-futuristic"
            whileHover={{ scale: 1.05, textShadow: "0 0 30px #00f5ff" }}
          >
            <FaPaperPlane className="title-icon" />
            Contact
          </motion.h2>
          <motion.p 
            className="section-subtitle-futuristic"
            whileHover={{ scale: 1.02 }}
          >
            Ready to collaborate on your next project
          </motion.p>
        </motion.div>
        
        <div className="contact-content-futuristic">
          <motion.div 
            className="contact-info-card"
            initial={{ opacity: 0, x: -100 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 1, delay: 0.2 }}
            whileHover={{ 
              scale: 1.02,
              boxShadow: "0 25px 50px rgba(0, 245, 255, 0.3)"
            }}
          >
            <div className="card-glow cyan"></div>
            <h3 className="contact-title">Get In Touch</h3>
            
            <div className="contact-methods">
              <motion.div 
                className="contact-method"
                whileHover={{ x: 10, scale: 1.05 }}
              >
                <FaEnvelope className="contact-icon" />
                <span>{personalInfo.email}</span>
              </motion.div>
              
              <motion.div 
                className="contact-method"
                whileHover={{ x: 10, scale: 1.05 }}
              >
                <FaPhone className="contact-icon" />
                <span>{personalInfo.phone}</span>
              </motion.div>
              
              <motion.div 
                className="contact-method"
                whileHover={{ x: 10, scale: 1.05 }}
              >
                <FaLinkedin className="contact-icon" />
                <span>LinkedIn Profile</span>
              </motion.div>
              
              <motion.div 
                className="contact-method"
                whileHover={{ x: 10, scale: 1.05 }}
              >
                <FaGithub className="contact-icon" />
                <span>GitHub Profile</span>
              </motion.div>
            </div>
          </motion.div>

          <motion.form 
            className="contact-form-futuristic"
            initial={{ opacity: 0, x: 100 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 1, delay: 0.4 }}
            onSubmit={handleSubmit}
          >
            <div className="form-glow"></div>
            
            <motion.div 
              className="form-group"
              whileHover={{ scale: 1.02 }}
            >
              <input
                type="text"
                name="name"
                placeholder="Your Name"
                value={formData.name}
                onChange={handleChange}
                className="form-input-futuristic"
                required
              />
              <div className="input-glow"></div>
            </motion.div>
            
            <motion.div 
              className="form-group"
              whileHover={{ scale: 1.02 }}
            >
              <input
                type="email"
                name="email"
                placeholder="Your Email"
                value={formData.email}
                onChange={handleChange}
                className="form-input-futuristic"
                required
              />
              <div className="input-glow"></div>
            </motion.div>
            
            <motion.div 
              className="form-group"
              whileHover={{ scale: 1.02 }}
            >
              <textarea
                name="message"
                placeholder="Your Message"
                value={formData.message}
                onChange={handleChange}
                className="form-textarea-futuristic"
                rows="5"
                required
              ></textarea>
              <div className="input-glow"></div>
            </motion.div>
            
            <motion.button
              type="submit"
              className="submit-btn-futuristic"
              disabled={isSubmitting}
              whileHover={!isSubmitting ? { 
                scale: 1.05,
                boxShadow: "0 0 30px rgba(0, 245, 255, 0.8)"
              } : {}}
              whileTap={!isSubmitting ? { scale: 0.95 } : {}}
            >
              <FaPaperPlane className="btn-icon" />
              {isSubmitting ? 'Sending...' : 'Send Message'}
            </motion.button>
          </motion.form>
        </div>
      </div>
    </section>
  );
};

export default Contact;